import React from 'react'
import { TransactionalDialog } from '@jarvis.oddle/core/components/Dialog'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'

import { useAppContext } from 'src/containers/AppContext'
import { applyPromotionsAction } from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { isObjectEmpty } from 'src/utils/object'
import { makeCombineString } from 'src/utils/string'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'

/**
 * A modal that will be shown when user is not eligible for
 * a credit card promotion after they send a checkout request
 */
const CheckoutFailedCardPromotionModal = React.memo(
  ({
    modal,
    error,
    type,
    orderPromotions,
    onCancelCallback,
    onConfirmCallback,
    customerSegmentEnabled,
    ...props
  }: {
    modal: {
      show: boolean
      onClose: () => void
      onClosed: () => void
    }
    error: {
      errorCode: string
      errorParams: {
        promotions: {
          name: string
          promotionReference: string
        }[]
        // eslint-disable-next-line camelcase
        masked_card_details: string
      }
    }[]
    type: string
    orderPromotions: {
      id: string
      promotionReference: string
      type: number
      amount: number
      name: string
      appliedItemReference: boolean
      resultAmountType: number
    }[]
    onCancelCallback: (params) => void
    onConfirmCallback: () => void
    customerSegmentEnabled: boolean
  }) => {
    const dispatch = useAppDispatch()
    const { t } = React.useContext(TranslationContext)
    const { prepareToCheckout } = useAppContext()
    if (isObjectEmpty(error)) {
      return null
    }

    const cardPromotionError = error.find(
      (err) => err.errorCode === 'payment_card_does_not_qualify_for_promotion'
    )
    if (isObjectEmpty(cardPromotionError)) {
      return
    }
    const { errorParams } = cardPromotionError

    /**
     * bannedPromotions contains array of promotion detail object.
     * Each element in the array represents an ineligible promotion
     */
    const {
      promotions: bannedPromotions,
      masked_card_details: maskedCardDetails
    } = errorParams || {}
    const bannedPromotionsReference = (bannedPromotions || []).map(
      (bannedPromo) => bannedPromo.promotionReference
    )

    /**
     * Concatenate all banned promotions into one sentence, i.e. 10% off, 20% off, 30% off
     */
    const promotionTexts = (bannedPromotions || [])
      .map((param) => param.name)
      .join(', ')

    const primaryText = t(
      'The credit card _maskedCardNumber_ used is not eligible for _promotion_.',
      {
        maskedCardNumber: maskedCardDetails,
        promotion: promotionTexts
      }
    )
    const secondaryText = t(
      'Would you like to proceed to checkout without promotion _promotion_?',
      {
        promotion: promotionTexts
      }
    )
    const promoErrorDescription = makeCombineString('\n\n')(
      primaryText,
      secondaryText
    )

    const onCancel = () => {
      typeof onCancelCallback === 'function' && onCancelCallback(primaryText)
    }

    const onConfirm = async () => {
      try {
        const allowedPromotions = orderPromotions.filter((promo) => {
          return !(bannedPromotionsReference || []).includes(
            promo.promotionReference
          )
        })

        const applyPromotionsResponse = await dispatch(applyPromotionsAction({
          promotions: allowedPromotions
        }))
        if (applyPromotionsResponse.meta.requestStatus !== 'fulfilled') {
          throw new Error(
            t('Oops! There seems to be a problem. Please try again later.')
          )
        }

        await prepareToCheckout({
          onReadyToCheckout: (storeOrder) => {
            modalController.showModal('CheckoutPromoRemovalModal', {
              storeOrder,
              type,
              customerSegmentEnabled
            })
          },
          onNotReadyToCheckout: (errors, errorCodes) => {
            modalController.showModal('CartErrorModal', { errors, errorCodes })
          },
          onShouldRefresh: modal.onClose
        })

        typeof onConfirmCallback === 'function' && onConfirmCallback()
      } catch (e) {
        showNotificationToast.error(e.message)
      }
    }

    return (
      <TransactionalDialog
        {...modal}
        {...props}
        closeOnBackdropClick={false}
        closeOnEscapeKeyPress={false}
        onConfirm={onConfirm}
        onCancel={onCancel}
        header={t('Oops')}
        cancelText={t('No')}
        confirmText={t('Yes')}
        body={promoErrorDescription}
      />
    )
  }
)

export default makeControlledModal(
  CheckoutFailedCardPromotionModal,
  'CheckoutFailedCardPromotionModal'
)
